import type { OnClickEvent } from '@/core/types/react-overrides';
import type {
    SchoolProduct,
    SchoolProductTilesV2Attributes,
} from '@/features/school-product-tiles-v2/school-product-tiles-v2-type';

import { Badge, KeyFactsDesktop, MediaSwiper } from '@/core/features';
import useWindowResize from '@/core/features/event-listener/use-window-resize';
import ConditionalLinkWrapper from '@/core/features/link/conditional-link-wrapper/conditional-link-wrapper';
import color from '@/core/features/styles/color';
import { useCartLocalStorage } from '@/features/cart/service/cart-state';
import CheckoutCancellationInfoDesktop from '@/features/checkout/checkout-shared-components/checkout-cancellation-info-desktop';
import SchoolDetailsOverlayDesktop from '@/features/school-product-tiles-v2/school-details-overlay/school-details-overlay-desktop';
import SchoolReservationOverlayDesktop from '@/features/school-product-tiles-v2/school-reservation-overlay/school-reservation-overlay-desktop';
import useSchoolProduct from '@/features/school-product-tiles-v2/use-school-product';

export type SchoolProductTilesV2Props = SchoolProductTilesV2Attributes;

export default function SchoolProductTilesV2Desktop({
    activitySpotId,
    cancellation,
    products,
    schoolId,
    schoolName,
    tabBar,
    tabBarTitle,
}: SchoolProductTilesV2Props) {
    const {
        calculateKeyFactList,
        effectiveCancellation,
        filteredProducts,
        isDetailsOverlayOpen,
        isReservationOverlayOpen,
        mergeCancellation,
        selectedProduct,
        selectedTab,
        setIsDetailsOverlayOpen,
        setIsReservationOverlayOpen,
        toggleDetailsOverlay,
        toggleReservationOverlay,
        toggleTab,
    } = useSchoolProduct(tabBar, products, cancellation);

    const { isProductInCart } = useCartLocalStorage({ activitySpotId, schoolId });
    const { windowSize } = useWindowResize();

    if (products.length === 0) {
        return null;
    }

    const handleTileClick = (product: SchoolProduct, event: OnClickEvent) => {
        if (event.isDefaultPrevented() || product.linkUrl) {
            return;
        }

        toggleDetailsOverlay(!isDetailsOverlayOpen, product);
    };

    return (
        <div className={'flex-column gap-15'}>
            <div
                className={'font-size-18 bold'}
                data-qa-id={'qa-school-products-tabs'}
            >
                {tabBarTitle}
            </div>
            {tabBar.length !== 0 && (
                <div className={'flex-column gap-15'}>
                    <div className={'no-scrollbar font-size-14 flex column-gap-10 align-center overflow-x-scroll'}>
                        {tabBar.map((tab, index) => {
                            const isSelected = selectedTab.includes(tab.name);
                            return (
                                <div
                                    className={`flex-center border text-nowrap pointer vertical-middle padding-10 border-radius-30 nowrap ${isSelected ? 'bg-color-foam border-endeavour' : 'bg-white border-alto'}`}
                                    data-qa-id={'qa-school-products-tabs'}
                                    key={`${tab.title}-${index}`}
                                    onClick={() => toggleTab(tab)}
                                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                                    style={{ height: 34 }}
                                >
                                    <div>{tab.title}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className={'flex-column gap-15'}>
                {filteredProducts.map((product, index) => {
                    const effectiveProductCancellation = mergeCancellation(product.cancellation);
                    const hasBadge = isProductInCart(product.productId);
                    const hasImage = product.gallery && product.gallery?.length > 0;

                    return (
                        <ConditionalLinkWrapper
                            href={product.linkUrl}
                            key={`${product.productName}-${index}`}
                            linkType={'internal'}
                        >
                            <div
                                className={'pointer'}
                                data-qa-id={'qa-school-products-detail-overlay-button'}
                                onClick={(event) => handleTileClick(product, event)}
                            >
                                <div
                                    className={'width-100 height-100 grid bg-white border border-alto border-radius-5'}
                                    style={{
                                        gridTemplateColumns:
                                            hasImage && windowSize.innerWidth >= 1400 ? '290px 1fr' : '1fr',
                                    }}
                                >
                                    {hasImage && (
                                        <MediaSwiper
                                            imageOptions={{
                                                height: 290,
                                                width: 290,
                                            }}
                                            items={product.gallery}
                                            swiperOptions={{
                                                debugName: 'SchoolProductTilesV2Desktop',
                                                loop: true,
                                                swiperModuleType: 'gallery',
                                            }}
                                        />
                                    )}
                                    <div className={'padding-15 relative'}>
                                        <div
                                            className={'line-clamp-2 margin-bottom-16 font-size-16 bold'}
                                            style={{ maxWidth: hasBadge ? '85%' : '100%' }}
                                        >
                                            {product.productName}
                                        </div>
                                        <div className={'absolute top-15 right-15'}>
                                            {hasBadge && (
                                                <Badge
                                                    badge={{
                                                        backgroundColor: color('bittersweet'),
                                                        htmlText: 'Im Warenkorb',
                                                        outlineColor: null,
                                                        textColor: color('white'),
                                                    }}
                                                />
                                            )}
                                        </div>
                                        {product.htmlShortDescriptionText && (
                                            <div>{product.htmlShortDescriptionText}</div>
                                        )}
                                        <div className={'margin-top-15 flex'}>
                                            <div className={'margin-right-10 flex-1'}>
                                                {product.keyfacts.length > 0 && (
                                                    <KeyFactsDesktop
                                                        gap={8}
                                                        iconSize={16}
                                                        keyFacts={calculateKeyFactList(product)}
                                                        truncation={'oneLine'}
                                                    />
                                                )}
                                            </div>
                                            <div className={'flex-end padding-top-20 shrink-0 flex-1'}>
                                                <div className={'flex-column gap-5'}>
                                                    {product.offer.strikePriceText && (
                                                        <div
                                                            className={
                                                                'width-100 text-end text-persian-red line-through font-size-12'
                                                            }
                                                        >
                                                            {product.offer.strikePriceText}
                                                        </div>
                                                    )}
                                                    <div
                                                        className={'flex-end width-100 gap-5'}
                                                        style={{ marginTop: -5 }}
                                                    >
                                                        <span
                                                            className={'font-size-14'}
                                                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                                                            style={{ paddingBottom: 2 }}
                                                        >
                                                            ab
                                                        </span>
                                                        <span className={'font-size-24'}>
                                                            {product.offer.priceText}
                                                        </span>
                                                    </div>

                                                    {effectiveProductCancellation && (
                                                        <CheckoutCancellationInfoDesktop
                                                            iconUrl={effectiveProductCancellation.condition.iconUrl}
                                                            overlayHtmlText={
                                                                effectiveProductCancellation.overlay
                                                                    ?.htmlDescriptionText
                                                            }
                                                            overlayHtmlTitle={
                                                                effectiveProductCancellation.overlay?.title
                                                            }
                                                            text={effectiveProductCancellation.condition.text}
                                                            textColor={effectiveProductCancellation.condition.color}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ConditionalLinkWrapper>
                    );
                })}
            </div>
            {/* [bc-26] remove all remaining overlays/cta which are obselete, because of the product detail page */}
            {isDetailsOverlayOpen && selectedProduct?.detailsOverlay && (
                <SchoolDetailsOverlayDesktop
                    cancellationOverlay={effectiveCancellation}
                    ctaReservationButton={selectedProduct.cta.ctaReservationButton}
                    gallery={selectedProduct.gallery}
                    htmlDescriptionText={selectedProduct.detailsOverlay.htmlDescriptionText}
                    keyfacts={selectedProduct.keyfacts}
                    onClose={() => setIsDetailsOverlayOpen(false)}
                    openReservationOverlay={() => toggleReservationOverlay(true, selectedProduct)}
                    priceText={selectedProduct.offer.priceText}
                    productName={selectedProduct.productName}
                    schoolName={schoolName}
                    strikePriceText={selectedProduct.offer.strikePriceText}
                />
            )}
            {isReservationOverlayOpen && selectedProduct?.reservationOverlay && (
                <SchoolReservationOverlayDesktop
                    activitySpotId={activitySpotId}
                    htmlHeaderText={selectedProduct.reservationOverlay.htmlHeaderText}
                    onClose={() => setIsReservationOverlayOpen(false)}
                    productId={selectedProduct.productId}
                    productName={selectedProduct.productName}
                    schoolId={schoolId}
                    schoolName={schoolName}
                    type={selectedProduct.reservationOverlay.type}
                />
            )}
        </div>
    );
}
